// src/components/MainNav.jsx
// The main menu (3.20.1): one button per tab, each on one line; 3.22.0:
// joined into one centred bar (the buttons wrap as whole buttons on smaller
// windows); on phones the menu folds into a single "☰" button.
// 3.39.0: on desktop the bar holds the everyday pages; the others (rail:
// true) are in the icon strip on the left (SideRail). When one of those is
// open, a chip for it ends the bar, so you always see where you are.
// 3.39.3: that page is now the bar's last button, in the same style as the
// others: the highlight slides over to it.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePhone } from '../hooks/useWindowWidth';
import { NAV, NAV_ON, NAV_BAR, NAV_SEG } from '../theme/tabs';
import Emo from './Emo';

export default function MainNav({ tabs, active, hrefFor, onGo, labelFor, alert = false, compact = false }) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  // 3.39.1: a coloured highlight that slides to the open tab
  const barRef = useRef(null);
  const [pill, setPill] = useState(null);   // { left, width } in the bar, or null
  useLayoutEffect(() => {
    const bar = barRef.current;
    const measure = () => {
      const a = bar?.querySelector('a[aria-current="page"]');
      const next = a ? { left: a.offsetLeft, top: a.offsetTop, width: a.offsetWidth, height: a.offsetHeight } : null;
      // Only when it moved, or this would redraw forever
      setPill(prev => (prev && next && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height) || (!prev && !next) ? prev : next);
    };
    measure();
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [active, tabs.length, phone]);
  useEffect(() => { setOpen(false); }, [active, phone]);

  const link = t => (
    <a key={t.id} href={hrefFor(t.id)} aria-current={active === t.id ? 'page' : undefined} data-tour={`tab-${t.id}`}
      onClick={e => {
        // Ctrl/Cmd/Shift/middle-click opens the tab's link normally
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onGo(t.id);
        // 3.47.3: a mouse click doesn't leave the item focused (a later key press would ring it)
        if (e.detail > 0) e.currentTarget.blur();
      }}
      className="nav-seg" style={phone ? { ...NAV, ...S.phoneItem, ...(active === t.id ? NAV_ON : {}) } : { ...NAV_SEG, ...S.seg, ...(compact ? S.segCompact : {}), ...(active === t.id ? S.segOn : {}) }}>
      {/* 3.51.0: tablets and small windows (compact): words only, so the bar fits on one line */}
      {!compact && <Emo e={t.icon} />} {labelFor(t)}
    </a>
  );

  if (phone) {
    const cur = tabs.find(t => t.id === active);
    return (
      <nav style={S.phoneWrap} aria-label="Main menu">
        <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ ...NAV, ...S.phoneToggle }}>
          <span>☰</span>{alert && !open && <span style={S.alertDot} aria-label="something new" />} {cur ? <><Emo e={cur.icon} /> {labelFor(cur)}</> : 'Menu'}
          <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
        </button>
        {open && <div style={S.phoneList}>{tabs.map(link)}</div>}
      </nav>
    );
  }
  // 3.22.0: one joined bar, centred
  const railOpen = tabs.find(t => t.rail && t.id === active);
  return (
    <nav style={S.row} aria-label="Main menu">
      <div style={S.stack}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <div ref={barRef} style={{ ...NAV_BAR, ...S.bar }}>
            {pill && <span aria-hidden="true" style={{ ...S.pill, left: pill.left, top: pill.top, width: pill.width, height: pill.height }} />}
            {tabs.filter(t => !t.rail).map(link)}
            {railOpen && link(railOpen)}
          </div>
        </div>
      </div>
    </nav>
  );
}

const S = {
  row: { display: 'flex', justifyContent: 'center', marginBottom: 0 },
  // 3.39.1: the bar is see-through white with a soft shadow; the open tab's
  // colour is a gradient pill that slides between tabs
  bar: { position: 'relative', background: 'rgb(var(--glass-rgb) / 0.85)', border: '1.5px solid var(--b-a5d6a7)', boxShadow: '0 4px 14px rgb(var(--c-00695c-rgb) / 0.12)', borderRadius: 14 },
  seg: { position: 'relative', zIndex: 1, background: 'transparent', boxShadow: 'none', transition: 'color .25s ease' },
  segOn: { color: 'var(--t-ffffff)' },
  segCompact: { padding: '9px 12px', fontSize: 13.5 },
  pill: { position: 'absolute', zIndex: 0, borderRadius: 11, background: 'linear-gradient(135deg, var(--c-00897b), var(--c-26a69a) 55%, var(--b-43a047))',
    boxShadow: '0 4px 14px rgb(var(--c-00897b-rgb) / 0.45), inset 0 1px 0 rgb(var(--c-ffffff-rgb) / 0.25)', transition: 'left .38s cubic-bezier(.34,1.4,.64,1), width .38s cubic-bezier(.34,1.4,.64,1), top .3s ease' },
  stack: { display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, maxWidth: '100%' },
  phoneWrap: { marginBottom: 18 },
  alertDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--c-d32f2f)', marginLeft: -2, marginTop: -10 },
  phoneToggle: { width: '100%', justifyContent: 'flex-start', padding: '11px 14px', fontSize: 15 },
  phoneList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 },
  phoneItem: { width: '100%', padding: '11px 14px', fontSize: 15 },
};
